import Confetti from "react-confetti";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const questions = [
  {
    question: "What is the first step in the research cycle as described in Chapter 4?",
    options: [
      "Describing the problem in detail",
      "Identifying a problem",
      "Reviewing existing information",
      "Narrowing down the research question",
    ],
    correctAnswer: 1,
  },
  {
    question: "What does carrying out a literature review involve?",
    options: [
      "Collecting new data from experiments",
      "Reviewing journals, reports, policies, and guidelines",
      "Implementing a solution immediately",
      "Designing new research methods",
    ],
    correctAnswer: 1,
  },
  {
    question: "Once a problem is identified, what is the next key step?",
    options: [
      "Describing the problem in detail",
      "Identifying potential reasons for the problem",
      "Collecting data through experiments",
      "Testing an intervention immediately",
    ],
    correctAnswer: 0,
  },
  {
    question: "Why is it important to identify potential causes of the problem?",
    options: [
      "To create a more general research question",
      "To fully understand the factors contributing to the problem",
      "To skip the literature review",
      "To design an intervention without data",
    ],
    correctAnswer: 1,
  },
  {
    question: "What does narrowing down your research question help you to do?",
    options: [
      "Address the entire problem at once",
      "Focus on a manageable aspect of the problem",
      "Avoid looking at available information",
      "Implement solutions without further study",
    ],
    correctAnswer: 1,
  },
];

const questionVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const QuizPopupChapter4 = ({ onClose, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showCorrections, setShowCorrections] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleNext = (skip = false) => {
    // Save answer as a number (or null if skipped)
    setAnswers((prev) => [...prev, skip ? null : Number(selectedOption)]);
    setSelectedOption(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateScore = () => {
    const correctCount = answers.reduce((acc, answer, index) => {
      return answer === questions[index].correctAnswer ? acc + 1 : acc;
    }, 0);
    return ((correctCount / questions.length) * 100).toFixed(0);
  };
  const score = calculateScore();
  const isPerfectScore = score === "100"; // Check if 100%
  useEffect(() => {
    if (isPerfectScore) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000); // Stop confetti after 5 seconds
    }
  }, [isPerfectScore]);

  if (showResults) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        {/* Overlay */}
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 overflow-y-auto max-h-full">
        {isPerfectScore && showConfetti && <Confetti numberOfPieces={300} gravity={0.2} />}
          {!showCorrections ? (
            <div className="text-center">
               <p className="text-6xl font-bold text-blue-600 mb-4">{score}%</p>
              <p className="text-lg mb-6">
                {isPerfectScore
                  ? "Congratulations! You got a perfect score! 🎉"
                  : `You got ${score}% of the questions right! Would you like to exit or check for corrections?`}
              </p>
              <div className="flex justify-around">
                <button
                  onClick={() => {
                    onComplete(answers);
                    onClose();
                  }}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition duration-300"
                >
                  Exit
                </button>
                <button
                  onClick={() => setShowCorrections(true)}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition duration-300"
                >
                  Check Corrections
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center">Corrections</h2>
              {questions.map((q, index) => (
                <div key={index} className="mb-4 border-b pb-2">
                  <p className="font-semibold">
                    Q{index + 1}: {q.question}
                  </p>
                  <p className="text-green-600">
                    Correct Answer: {q.options[q.correctAnswer]}
                  </p>
                  <p className="text-gray-700">
                    Your Answer:{" "}
                    {answers[index] !== null ? q.options[answers[index]] : "Skipped"}
                  </p>
                </div>
              ))}
              <button
                onClick={() => {
                  onComplete(answers);
                  onClose();
                }}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300 w-full"
              >
                Exit
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentData = questions[currentQuestion];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black opacity-50"></div>
      {/* Modal Content */}
      <div className="relative bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            variants={questionVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-4">
              Question {currentQuestion + 1} of {questions.length}
            </h2>
            <p className="mb-4">{currentData.question}</p>
            <form>
              {currentData.options.map((option, index) => (
                <div key={index} className="mb-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="question"
                      value={index}
                      checked={selectedOption === String(index)}
                      onChange={handleOptionChange}
                      className="mr-2"
                    />
                    {option}
                  </label>
                </div>
              ))}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => handleNext(true)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition duration-300"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={() => handleNext(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                  disabled={selectedOption === null}
                >
                  Submit
                </button>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuizPopupChapter4;
